import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow, formatDistanceToNowStrict } from 'date-fns';
import { ArrowRight, LayoutGrid, List, PenSquare } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getPlatformDisplayName } from '@/lib/utils';
import { useLogSurfaceVisit } from '@/hooks/useActivityLog';
import { FeedPlatformIcon } from '@/components/dashboard/FeedPlatformIcon';
import { ComposeDialog } from '@/components/dashboard/ComposeDialog';
import { FeaturedCurators } from '@/components/FeaturedCurators';

const STALE_MS = 60_000;
const FEED_LIMIT = 150;
const ITEMS_PER_TILE = 3;

// ── Types ────────────────────────────────────────────────────────────────────

interface FeedItem {
  id: string;
  platform: string;
  author: string | null;
  title: string | null;
  body: string | null;
  url: string | null;
  published_at: string | null;
  fetched_at: string;
  is_filtered: boolean;
  is_pending_review: boolean;
  source_name: string | null;
  content_json: any;
}

interface SourceData {
  key: string;
  platform: string;
  displayName: string;
  newCount: number;
  totalCount: number;
  items: FeedItem[];
}

type ViewMode = 'tile' | 'list';

// ── Media extraction (mirrors TopNavFeeds) ────────────────────────────────────

function extractImage(item: FeedItem): string | null {
  const cj = item.content_json;
  if (!cj) return null;

  if (item.platform === 'rss') {
    return cj.image_url ?? null;
  }
  if (item.platform === 'youtube') {
    const t = cj.snippet?.thumbnails;
    return t?.medium?.url || t?.high?.url || t?.maxres?.url || null;
  }
  if (item.platform === 'bluesky') {
    const embed = cj.embed;
    if (!embed) return null;
    const type: string = embed.$type ?? '';
    if (type === 'app.bsky.embed.images#view') {
      return embed.images?.[0]?.thumb || embed.images?.[0]?.fullsize || null;
    }
    if (type === 'app.bsky.embed.external#view') return embed.external?.thumb ?? null;
    if (type === 'app.bsky.embed.video#view') return embed.thumbnail ?? null;
    return null;
  }
  if (item.platform === 'reddit') {
    if (cj.media_metadata) {
      const first = Object.values(cj.media_metadata as Record<string, any>)
        .find((m: any) => m.status === 'valid' && m.e === 'Image');
      const src = first?.s?.u || first?.p?.[first.p.length - 1]?.u;
      if (src) return (src as string).replace(/&amp;/g, '&');
    }
    const prev = cj.preview?.images?.[0]?.source?.url;
    if (prev) return (prev as string).replace(/&amp;/g, '&');
    const thumb = cj.thumbnail as string | undefined;
    if (thumb && !['self', 'default', 'nsfw', 'spoiler', 'image', ''].includes(thumb) && thumb.startsWith('http')) {
      return thumb;
    }
  }
  return null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function greetingForHour(hour: number): string {
  if (hour < 5)  return 'Good evening';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function deriveFirstName(
  fullName: string | null,
  email: string | null,
  metadataName: string | null,
): string {
  const fromProfile = fullName?.trim();
  if (fromProfile && /^[A-Za-zÀ-ÿ]+([ '-][A-Za-zÀ-ÿ]+)+$/.test(fromProfile)) {
    return fromProfile.split(/\s+/)[0];
  }
  if (metadataName) {
    const first = metadataName.trim().split(/\s+/)[0];
    if (first) return first;
  }
  if (email) {
    const localPart = email.split('@')[0];
    return localPart.charAt(0).toUpperCase() + localPart.slice(1);
  }
  return '';
}

// ── Safe image component ──────────────────────────────────────────────────────

function SafeImg({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
      loading="lazy"
    />
  );
}

// ── Source tile card ──────────────────────────────────────────────────────────

function SourceTile({
  source,
  isNew,
  onClick,
}: {
  source: SourceData;
  isNew: boolean;
  onClick: () => void;
}) {
  return (
    <Card
      onClick={onClick}
      className={`flex flex-col cursor-pointer hover:shadow-md transition-all duration-150 overflow-hidden ${
        isNew ? 'ring-1 ring-[#1A2E4A]/20' : ''
      }`}
    >
      {/* ── Tile header ── */}
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b bg-muted/20">
        <div className="flex items-center gap-2 min-w-0">
          <FeedPlatformIcon
            platform={source.platform}
            className="h-4 w-4 text-muted-foreground shrink-0"
          />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">
            {source.displayName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          {source.newCount > 0 && (
            <Badge className="text-[10px] py-0 px-1.5 h-4 bg-[#1A2E4A] text-white hover:bg-[#1A2E4A]">
              {source.newCount} new
            </Badge>
          )}
          <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-4">
            {source.totalCount}
          </Badge>
        </div>
      </div>

      {/* ── Item previews ── */}
      <div className="flex-1 divide-y divide-border/40">
        {source.items.slice(0, ITEMS_PER_TILE).map((item) => {
          const imgSrc = extractImage(item);
          const label = item.title || item.body?.slice(0, 100) || null;
          const timeAgo = item.fetched_at
            ? formatDistanceToNow(new Date(item.fetched_at), { addSuffix: true })
            : null;

          return (
            <div key={item.id} className="flex gap-2.5 px-3.5 py-2.5">
              {/* Thumbnail */}
              {imgSrc && (
                <div className="shrink-0 w-12 h-12 rounded overflow-hidden bg-muted">
                  <SafeImg
                    src={imgSrc}
                    alt={label ?? 'Image'}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {/* Text */}
              <div className="min-w-0 flex-1">
                {label && (
                  <p className="text-xs font-medium leading-snug line-clamp-2 text-foreground/90">
                    {label}
                  </p>
                )}
                {timeAgo && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo}</p>
                )}
              </div>
            </div>
          );
        })}

        {source.items.length === 0 && (
          <div className="px-3.5 py-4 text-xs text-muted-foreground italic">
            No posts yet.
          </div>
        )}
      </div>

      {/* ── Tile footer ── */}
      <div className="px-3.5 py-2 border-t bg-muted/10 flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground">
          {source.totalCount} total
        </span>
        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
          Open feed <ArrowRight className="h-2.5 w-2.5" />
        </span>
      </div>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function TopNavOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [composeOpen, setComposeOpen] = React.useState(false);
  useLogSurfaceVisit('overview');

  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem('home_view_mode') as ViewMode | null) ?? 'tile',
  );
  const [digest, setDigest] = useState<string | null>(null);
  const [digestLoading, setDigestLoading] = useState(false);

  const setAndSaveView = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('home_view_mode', mode);
  };

  // ── Data fetching ──────────────────────────────────────────────────────────

  const profileQuery = useQuery({
    queryKey: ['profile-home', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, last_seen_at')
        .eq('id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? { full_name: null, last_seen_at: null }) as {
        full_name: string | null;
        last_seen_at: string | null;
      };
    },
    staleTime: STALE_MS,
  });

  const itemsQuery = useQuery<FeedItem[]>({
    queryKey: ['home-feed-summary', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_feed_items')
        .select(
          'id, platform, author, title, body, url, published_at, fetched_at, is_filtered, is_pending_review, source_name, content_json',
        )
        .eq('user_id', user!.id)
        .eq('is_filtered', false)
        .eq('is_pending_review', false)
        .order('fetched_at', { ascending: false })
        .limit(FEED_LIMIT);
      if (error) throw error;
      return (data ?? []) as FeedItem[];
    },
    staleTime: STALE_MS,
  });

  // ── Derived state ──────────────────────────────────────────────────────────

  const lastSeen = profileQuery.data?.last_seen_at ?? null;
  const fullName = profileQuery.data?.full_name ?? null;
  const items = itemsQuery.data ?? [];

  const firstName = deriveFirstName(
    fullName,
    user?.email ?? null,
    (user?.user_metadata?.full_name as string | undefined) ?? null,
  );

  const greeting = greetingForHour(new Date().getHours());
  const today = format(new Date(), 'EEEE, MMMM d');

  const sources = useMemo((): SourceData[] => {
    const cutoff = lastSeen ? new Date(lastSeen).getTime() : 0;
    const map = new Map<string, SourceData>();

    for (const item of items) {
      const key =
        item.platform === 'rss'
          ? `rss:${item.source_name ?? 'RSS'}`
          : item.platform;

      if (!map.has(key)) {
        map.set(key, {
          key,
          platform: item.platform,
          displayName:
            item.platform === 'rss'
              ? (item.source_name ?? 'RSS')
              : getPlatformDisplayName(item.platform),
          newCount: 0,
          totalCount: 0,
          items: [],
        });
      }

      const entry = map.get(key)!;
      entry.totalCount += 1;
      if (cutoff && new Date(item.fetched_at).getTime() > cutoff) {
        entry.newCount += 1;
      }
      // Keep top ITEMS_PER_TILE items (already sorted newest-first by query)
      if (entry.items.length < ITEMS_PER_TILE) {
        entry.items.push(item);
      }
    }

    return Array.from(map.values()).sort((a, b) => b.newCount - a.newCount);
  }, [items, lastSeen]);

  const totalNew = sources.reduce((acc, s) => acc + s.newCount, 0);
  const isLoading = profileQuery.isLoading || itemsQuery.isLoading;

  // Fetch AI feed digest when there are new items — cached per session
  useEffect(() => {
    if (isLoading || totalNew === 0 || digest !== null || digestLoading) return;
    const cacheKey = `feed_digest_${lastSeen ?? 'none'}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) { setDigest(cached); return; }

    const newItems = items
      .filter((i) => lastSeen && new Date(i.fetched_at).getTime() > new Date(lastSeen).getTime())
      .slice(0, 25)
      .map((i) => ({ title: i.title, body: i.body, platform: i.platform, source_name: i.source_name }));

    if (!newItems.length) return;

    setDigestLoading(true);
    const sinceLabel = lastSeen
      ? formatDistanceToNowStrict(new Date(lastSeen), { addSuffix: true })
      : 'your last visit';

    supabase.functions
      .invoke('ai-assistant', { body: { action: 'feed_digest', items: newItems, since_label: sinceLabel } })
      .then(({ data }) => {
        const summary: string | null = data?.summary ?? null;
        if (summary) {
          setDigest(summary);
          sessionStorage.setItem(cacheKey, summary);
        }
      })
      .catch(() => { /* silent */ })
      .finally(() => setDigestLoading(false));
  }, [isLoading, totalNew, digest, digestLoading, items, lastSeen]);

  // Navigate to Feeds. With a platform key, opens the By-platform tab
  // and scrolls directly to that column.
  const goToFeeds = (platformKey?: string) => {
    if (platformKey) {
      navigate(`/dashboard/feeds?tab=by-platform&platform=${encodeURIComponent(platformKey)}`);
    } else {
      navigate('/dashboard/feeds');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="px-6 py-8 space-y-8">

      {/* ── Top bar: greeting + view toggle ────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-0.5">
          <h1 className="text-3xl font-light tracking-tight">
            {greeting}{firstName ? `, ${firstName}` : ''}.
          </h1>
          <p className="text-sm text-muted-foreground">{today}</p>
        </div>

        <div className="flex items-center gap-1.5 shrink-0 mt-1">
          {/* View toggle */}
          <Button
            variant={viewMode === 'tile' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setAndSaveView('tile')}
            title="Tile view"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setAndSaveView('list')}
            title="List view"
          >
            <List className="h-4 w-4" />
          </Button>

          {/* Compose */}
          <Button
            size="sm"
            className="h-8 bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white ml-2"
            onClick={() => setComposeOpen(true)}
          >
            <PenSquare className="h-3.5 w-3.5 mr-1.5" />
            Compose
          </Button>
        </div>
      </div>

      {/* ── Summary line ────────────────────────────────────────────────── */}
      {!isLoading && (
        <p className="text-base text-muted-foreground -mt-4">
          {totalNew > 0 ? (
            <>
              <span className="text-foreground font-medium">{totalNew} new {totalNew === 1 ? 'post' : 'posts'}</span>
              {' '}across{' '}
              <span className="text-foreground font-medium">
                {sources.filter((s) => s.newCount > 0).length}{' '}
                {sources.filter((s) => s.newCount > 0).length === 1 ? 'source' : 'sources'}
              </span>
              {' '}since your last visit.
            </>
          ) : sources.length > 0 ? (
            "You're all caught up."
          ) : (
            'Connect a platform or add an RSS source to start seeing content here.'
          )}
        </p>
      )}

      {/* ── AI feed digest ───────────────────────────────────────────────── */}
      {!isLoading && (digest || digestLoading) && (
        <p className="text-sm text-muted-foreground/80 italic -mt-4 leading-relaxed max-w-xl">
          {digestLoading && !digest
            ? <span className="inline-block h-4 w-64 bg-muted rounded animate-pulse" />
            : digest}
        </p>
      )}

      {/* ── Loading skeletons ────────────────────────────────────────────── */}
      {isLoading && viewMode === 'tile' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="overflow-hidden">
              <div className="px-3.5 py-2.5 border-b bg-muted/20 flex items-center gap-2">
                <div className="h-4 w-4 rounded-full bg-muted animate-pulse" />
                <div className="h-3 w-20 bg-muted rounded animate-pulse" />
              </div>
              <div className="divide-y divide-border/40">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="flex gap-2.5 px-3.5 py-2.5">
                    <div className="h-12 w-12 rounded bg-muted animate-pulse shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 bg-muted rounded animate-pulse w-full" />
                      <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                      <div className="h-2.5 bg-muted rounded animate-pulse w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {isLoading && viewMode === 'list' && (
        <div className="max-w-xl divide-y divide-border/30">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3.5">
                <div className="h-4 w-4 rounded-full bg-muted animate-pulse" />
                <div className="space-y-1.5">
                  <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                  <div className="h-2.5 w-40 bg-muted rounded animate-pulse" />
                </div>
              </div>
              <div className="h-3 w-10 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
      )}

      {/* ── Tile view ────────────────────────────────────────────────────── */}
      {!isLoading && viewMode === 'tile' && sources.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources.map((source) => (
            <SourceTile
              key={source.key}
              source={source}
              isNew={source.newCount > 0}
              onClick={() => goToFeeds(source.key)}
            />
          ))}
        </div>
      )}

      {/* ── List view ────────────────────────────────────────────────────── */}
      {!isLoading && viewMode === 'list' && sources.length > 0 && (
        <div className="max-w-xl divide-y divide-border/30">
          {sources.map((source) => (
            <div
              key={source.key}
              onClick={() => goToFeeds(source.key)}
              className="flex items-center justify-between py-4 cursor-pointer group"
            >
              <div className="flex items-center gap-3.5 min-w-0">
                <FeedPlatformIcon
                  platform={source.platform}
                  className="h-4 w-4 text-muted-foreground shrink-0"
                />
                <div className="min-w-0">
                  <span className="text-sm font-medium">{source.displayName}</span>
                  {source.items[0] && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5 max-w-xs">
                      {source.items[0].title || source.items[0].body?.slice(0, 80)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-4">
                {source.newCount > 0 ? (
                  <span className="text-xs font-medium text-foreground/70 tabular-nums">
                    {source.newCount} new
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground/40 tabular-nums">
                    {source.totalCount}
                  </span>
                )}
                <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────── */}
      {!isLoading && sources.length === 0 && (
        <div className="py-20 text-center text-muted-foreground text-sm">
          <p className="font-medium mb-1">Nothing here yet.</p>
          <p>Connect a platform or add an RSS source to a rule to see content here.</p>
        </div>
      )}

      {/* ── Footer action ────────────────────────────────────────────────── */}
      {!isLoading && sources.length > 0 && (
        <button
          onClick={goToFeeds}
          className="flex items-center gap-1.5 text-sm text-foreground/50 hover:text-foreground transition-colors"
        >
          Open full feed
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}

      {/* ── Featured curators ─────────────────────────────────────────────── */}
      {!isLoading && (
        <div className="max-w-sm">
          <FeaturedCurators />
        </div>
      )}

      <ComposeDialog open={composeOpen} onOpenChange={setComposeOpen} />
    </div>
  );
}

export default TopNavOverview;
